import { OktaAuth } from '@okta/okta-auth-js';

// Check if debug mode is enabled
function isDebugModeEnabled(): boolean {
  const sophiaApiKey = import.meta.env.VITE_SOPHIA_API_KEY;
  const debugLogin = import.meta.env.VITE_DEBUG_LOGIN;
  
  return !!(sophiaApiKey && debugLogin);
}

function requireEnv(key: 'VITE_OKTA_ISSUER' | 'VITE_OKTA_CLIENT_ID'): string {
  const value = import.meta.env[key];

  if (!value) {
    throw new Error(`Missing ${key} environment variable for Okta configuration.`);
  }

  return value;
}

// URL opzionale (assoluto o relativo all'origine) di un endpoint OAuth servito dal nostro nginx.
// Serve con le app Okta "Web" con client secret: nginx aggiunge le credenziali del client
// e inoltra a Okta, così il secret non finisce mai nel bundle.
function optionalUrl(key: 'VITE_OKTA_TOKEN_URL' | 'VITE_OKTA_REVOKE_URL'): string | undefined {
  const value = import.meta.env[key];

  if (!value) {
    return undefined;
  }

  return new URL(value, window.location.origin).toString();
}

// Chiave di default con cui okta-auth-js salva i token in localStorage.
const OKTA_TOKEN_STORAGE_KEY = 'okta-token-storage';

// Scarta i token salvati da un'altra app Okta (client id diverso, es. dopo il cambio di app).
// okta-auth-js li riuserebbe per rinnovo e logout, e Okta risponde 400 perché non sono
// di questo client: meglio rifare il login da zero.
function clearTokensOfOtherClients(clientId: string): void {
  try {
    const raw = window.localStorage.getItem(OKTA_TOKEN_STORAGE_KEY);
    if (!raw) {
      return;
    }

    const stored = JSON.parse(raw) as Record<string, { clientId?: string; claims?: { cid?: string } }>;
    const otherClient = Object.values(stored).some((token) => {
      const tokenClientId = token?.clientId ?? token?.claims?.cid;
      return Boolean(tokenClientId) && tokenClientId !== clientId;
    });

    if (otherClient) {
      window.localStorage.removeItem(OKTA_TOKEN_STORAGE_KEY);
    }
  } catch (error) {
    console.warn('Unable to check stored Okta tokens', error);
  }
}

// Helper function to create a valid JWT token for debug mode
function createDebugJWT(): string {
  // Create a minimal valid JWT structure with base64url encoded parts
  const header = {
    alg: 'none',
    typ: 'JWT'
  };
  
  const payload = {
    sub: 'debug-user-123',
    uid: 'debug-user-123',
    name: 'Debug User',
    email: 'debug@example.com',
    preferred_username: 'debug-user',
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 3600 // 1 hour
  };
  
  // Base64url encode (without padding)
  const base64UrlEncode = (obj: any): string => {
    return btoa(JSON.stringify(obj))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  };
  
  const encodedHeader = base64UrlEncode(header);
  const encodedPayload = base64UrlEncode(payload);
  
  // For debug mode, we use an unsigned JWT (signature is empty)
  return `${encodedHeader}.${encodedPayload}.`;
}

// Create a mock oktaAuth object for debug mode
function createMockOktaAuth() {
  return {
    signInWithRedirect: () => Promise.resolve(),
    setOriginalUri: () => {},
    getUser: () => Promise.resolve({
      sub: 'debug-user-123',
      name: 'Debug User',
      email: 'debug@example.com',
      preferred_username: 'debug-user',
    }),
    getAccessToken: () => Promise.resolve(createDebugJWT()),
    signOut: () => Promise.resolve(),
    // Add other necessary mock methods as needed
  } as any;
}

// Create oktaAuth instance based on debug mode
function createOktaAuth() {
  if (isDebugModeEnabled()) {
    return createMockOktaAuth();
  }
  
  const clientId = requireEnv('VITE_OKTA_CLIENT_ID');
  clearTokensOfOtherClients(clientId);

  return new OktaAuth({
    issuer: requireEnv('VITE_OKTA_ISSUER'),
    clientId,
    redirectUri: import.meta.env.VITE_OKTA_REDIRECT_URI ?? `${window.location.origin}/login/callback`,
    postLogoutRedirectUri: import.meta.env.VITE_OKTA_POST_LOGOUT_REDIRECT_URI ?? window.location.origin,
    scopes: (import.meta.env.VITE_OKTA_SCOPES ?? 'openid profile email offline_access').split(' '),
    pkce: true,
    tokenUrl: optionalUrl('VITE_OKTA_TOKEN_URL'),
    revokeUrl: optionalUrl('VITE_OKTA_REVOKE_URL'),
    responseType: ["code", "refresh_token"],

    tokenManager: {
      autoRenew: true,
      autoRemove: false,
    },
    services: {
      autoRenew: false,
      autoRemove: false,
    },
  });
}

const oktaAuth = createOktaAuth();

export default oktaAuth;
export { isDebugModeEnabled };
